import React, { useState, useMemo, useRef } from "react";
import { Badge, colors } from "../../design/badge";
import { XMarkIcon } from "@heroicons/react/20/solid";
import { Input } from "../../design/input";
import EventTagDialog from "../Dialogs/EventTagDialog/EventTagDialog";
import { useEventTag } from "../../contexts/EventTagContext";
import { EventTag } from "../../models/EventTag";
import { Text } from "../../design/text";
import SelectEventTagDialog from "../Dialogs/SelectEventTagDialog/SelectEventTagDialog";

interface SmartTagInputProps {
  value: EventTag["_id"][];
  onChange: (tags: EventTag["_id"][]) => void;
  disabled?: boolean;
}

export const SmartTagInput: React.FC<SmartTagInputProps> = ({
  value,
  onChange,
  disabled,
}) => {
  const { eventTags: existingTags } = useEventTag();
  const [input, setInput] = useState("");
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const blurTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const filteredSuggestions = useMemo(() => {
    const query = input.toLowerCase();
    return existingTags
      .filter(tag => !value.includes(tag.name) && tag.name.toLowerCase().includes(query))
  }, [input, value, existingTags]);

  const handleAddTag = (tag: EventTag["_id"]) => {
    if (!value.includes(tag)) {
      onChange([...value, tag]);
    }
    setInput("");
  };

  const handleRemoveTag = (tag: string) => {
    onChange(value.filter(t => t !== tag));
  };

  const handleCreate = (newTag: EventTag) => {
    handleAddTag(newTag._id);
    setShowCreateDialog(false);
  };

  const handleFocus = () => {
    if (blurTimeoutRef.current) clearTimeout(blurTimeoutRef.current);
    setIsFocused(true);
  };

  const handleBlur = () => {
    blurTimeoutRef.current = setTimeout(() => setIsFocused(false), 100);
  };

  return (
    <div className="relative space-y-2">
      <div className="flex flex-wrap items-center gap-2 p-3 border rounded-lg">
        {value.map(tag => {
            const existingTag = existingTags.find(t => t._id === tag);
            if (!existingTag) return null;
            return (
                <Badge key={"ExistingTag"+existingTag._id} color={existingTag.color as keyof typeof colors} onClick={() => handleAddTag(existingTag._id)}>
            {existingTag.name}
            {!disabled && (
              <button onClick={() => handleRemoveTag(tag)}>
                <XMarkIcon className="w-3 h-3" />
              </button>
            )}
          </Badge>
            )
        })}
        <Input
        className="border-none focus:ring-0 focus:outline-none"
          type="text"
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => {
            if (e.key === "Enter") {
              e.preventDefault();
              const exactMatch = existingTags.some(tag => tag.name.toLowerCase() === input.toLowerCase());
              if (exactMatch) {
                handleAddTag(input);
              } else {
                setShowCreateDialog(true);
              }
            }
          }}
          onFocus={handleFocus}
          onBlur={handleBlur}
          placeholder="Type to pick or create tag..."
          disabled={disabled}
        />
      </div>

      {isFocused && filteredSuggestions.length > 0 && (
        <div className="absolute z-10 w-full max-w-lg p-2 mt-1 bg-white border rounded-md shadow-md dark:bg-black">
            <div className="flex items-center justify-between">
          <Text className="mb-1 text-xs">Tags</Text>
          <Text onClick={() => setShowEditDialog(true)}>Edit</Text>
        </div>
          <div className="flex flex-wrap gap-2">
            {filteredSuggestions.sort((a, b) => a.name.localeCompare(b.name)).map(suggestion => (
              <Badge
                key={"FilteredTag" + suggestion._id}
                color={suggestion.color as keyof typeof colors}
                onClick={() => handleAddTag(suggestion._id)}
                className="cursor-pointer hover:opacity-80"
              >
                {suggestion.name}
              </Badge>
            ))}
          </div>
        </div>
      )}

      <EventTagDialog
        name={input}
        isOpen={showCreateDialog}
        setIsOpen={setShowCreateDialog}
        onEventTagCreated={handleCreate}
      />
      <SelectEventTagDialog
        isOpen={showEditDialog}
        setIsOpen={setShowEditDialog}
        eventTags={existingTags}
        onEventTagSelected={(tag) => {
            setShowEditDialog(false);
            handleAddTag(tag._id);
        }}
        />
    </div>
  );
};